/**
 * Errors that mean the portal worked as intended even though the customer's
 * request did not succeed: an expired session, a declined card, bad input.
 * logger.error records these as warnings instead of reporting them to
 * GlitchTip, so a catch-all handler can log whatever it caught and only real
 * faults raise an alert.
 */
export class ExpectedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ExpectedError'
  }
}

/** The request has no valid session, or the session belongs to another customer. */
export class AuthenticationError extends ExpectedError {
  constructor(message: string) {
    super(message)
    this.name = 'AuthenticationError'
  }
}

/**
 * Thrown in the browser when a portal API call returns a non-OK status. A 4xx
 * (the session expired, the input was rejected) is expected. A 5xx is still
 * reported: the server should already have reported its side, but the extra
 * report says which screen the customer was on when it failed.
 */
export class ResponseError extends Error {
  status: number

  constructor(message: string, response: Response) {
    super(message)
    this.name = 'ResponseError'
    this.status = response.status
  }
}

export const isExpectedError = (error: unknown): boolean =>
  error instanceof ExpectedError || (error instanceof ResponseError && error.status < 500)
